import { Link } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import SectionHeading from "@/components/feature/SectionHeading";
import FilmCard from "@/components/feature/FilmCard";
import { films } from "@/mocks/films";
import useDocumentMeta from "@/hooks/useDocumentMeta";
import HeroCarousel from "./components/HeroCarousel";
import CategoryQuickLinks from "./components/CategoryQuickLinks";
import LatestUpdates from "./components/LatestUpdates";
import NewsBoard from "./components/NewsBoard";

export default function Home() {
  useDocumentMeta({
    title: "韩国AV_最新影视作品资源与剧情资讯平台",
    description:
      "韩国AV网站提供丰富的影视作品资源和最新剧情资讯，涵盖多种题材，方便用户浏览和查询影视相关内容。",
    keywords: "韩国AV,影视作品,剧情介绍,资源下载,最新影视,影视资讯,韩国电影",
  });

  const latestFilms = [...films]
    .sort((a, b) => (a.updatedAt < b.updatedAt ? 1 : -1))
    .slice(0, 6);

  return (
    <div className="flex min-h-screen w-full flex-col bg-background-50">
      <SiteHeader />

      <main className="mx-auto w-full max-w-[1280px] flex-1 px-4 pb-4 pt-6 md:px-6 md:pt-8 lg:px-8">
        <div className="mb-5 flex flex-col gap-2 md:mb-6 md:flex-row md:items-end md:justify-between">
          <div>
            <h1 className="font-heading text-[26px] font-semibold leading-tight tracking-tight text-foreground-950 md:text-[32px]">
              韩国AV · 欢迎来到韩国影视资料库
            </h1>
            <p className="mt-2 max-w-2xl text-[13px] leading-6 text-foreground-600">
              收录韩国电影与电视剧的作品资料、剧情简介与题材分类，帮助影迷快速找到想看的故事。
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-[11px] text-foreground-500">
            <span className="flex items-center gap-1.5">
              <i className="ri-film-line text-accent-600" />
              收录 {films.length} 部作品
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-price-tag-3-line text-accent-600" />
              9 个题材分类
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-refresh-line text-accent-600" />
              每周持续更新
            </span>
          </div>
        </div>

        <div className="animate-fade-up">
          <HeroCarousel />
        </div>

        <div className="mt-10 grid grid-cols-1 gap-8 lg:grid-cols-[minmax(0,1fr)_324px]">
          <div className="min-w-0">
            <div className="mb-3 flex items-center justify-between">
              <h2 className="text-[15px] font-semibold text-foreground-950">分类快捷入口</h2>
              <Link to="/category" className="flex items-center gap-1 text-[12px] text-foreground-500 transition-colors hover:text-primary-700">
                全部分类
                <i className="ri-arrow-right-s-line" />
              </Link>
            </div>
            <CategoryQuickLinks />

            <div className="mt-10">
              <SectionHeading
                eyebrow="Now Updated"
                title="最新作品"
                description="按资料更新时间排序，包含新上线条目与剧情信息补充。"
                actionLabel="查看全部作品"
                actionPath="/category"
              />
              {latestFilms.length === 0 ? (
                <div className="rounded-lg border border-dashed border-background-300 bg-background-100 px-6 py-16 text-center">
                  <p className="text-sm text-foreground-600">暂无内容，稍后再来看看</p>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-4">
                  {latestFilms.map((film) => (
                    <FilmCard key={film.id} film={film} />
                  ))}
                </div>
              )}
            </div>
          </div>

          <LatestUpdates />
        </div>

        <NewsBoard />

        <section className="mt-12 overflow-hidden rounded-lg border border-background-200/90 bg-background-100">
          <div className="grid grid-cols-1 gap-6 p-6 md:grid-cols-[1.4fr_1fr] md:items-center md:p-8">
            <div>
              <span className="text-[11px] font-medium uppercase tracking-[0.18em] text-accent-700">About This Site</span>
              <h2 className="mt-2 font-heading text-[22px] font-semibold text-foreground-950">
                我们只做一件事：把作品资料整理清楚
              </h2>
              <p className="mt-3 max-w-2xl text-[13px] leading-7 text-foreground-600">
                韩国AV 是一个由影迷维护的影视资料站。我们不提供播放与下载，只整理作品的基本信息、剧情脉络与观看指引，让每一次搜索都有明确的结果。所有条目均标注更新时间，方便你判断内容的新旧。
              </p>
              <div className="mt-5 flex flex-wrap items-center gap-3">
                <Link
                  to="/about"
                  className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-[13px] font-medium text-background-50 transition-colors hover:bg-primary-600"
                >
                  了解站点定位
                  <i className="ri-arrow-right-line text-sm" />
                </Link>
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-[13px] text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700"
                >
                  提交资料补充
                </Link>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {[
                { icon: "ri-search-eye-line", title: "模糊搜索", desc: "名称 / 题材 / 剧情" },
                { icon: "ri-layout-grid-line", title: "多维筛选", desc: "题材 / 年份 / 评分" },
                { icon: "ri-bookmark-3-line", title: "本地收藏", desc: "保存在你的浏览器" },
                { icon: "ri-share-forward-line", title: "一键分享", desc: "复制作品链接" },
              ].map((item) => (
                <div key={item.title} className="rounded-md border border-background-200 bg-background-50 p-3">
                  <span className="flex h-8 w-8 items-center justify-center rounded bg-primary-50 text-primary-600">
                    <i className={`${item.icon} text-sm`} />
                  </span>
                  <p className="mt-2.5 text-[12px] font-medium text-foreground-900">{item.title}</p>
                  <p className="mt-0.5 text-[10px] leading-5 text-foreground-500">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}